import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { MOCK_COURSES, MOCK_CATEGORIES } from '../utils/mockData';

function CourseFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        instructor: '',
        instructor_bio: '',
        instructor_image: '',
        category_id: '',
        price: '',
        thumbnail: '',
        preview_video: '',
        level: 'Beginner',
        language: 'English',
        lessons: [],
    });

    const [lessonForm, setLessonForm] = useState({
        title: '',
        description: '',
        video_url: '',
        duration: '',
        order: 0,
    });

    // Load course data if editing
    useEffect(() => {
        if (isEditing) {
            const course = MOCK_COURSES.find((c) => c.id === parseInt(id));
            if (course) {
                setFormData(course);
            } else {
                toast.error('Course not found');
                navigate('/admin?tab=courses');
            }
        }
    }, [id, isEditing, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // In a real app, this would save to backend
        // For now, just simulate success
        if (isEditing) {
            toast.success('Course updated successfully (demo only)');
        } else {
            toast.success('Course created successfully (demo only)');
        }

        // Navigate back to courses list
        navigate('/admin?tab=courses');
    };

    const addLesson = () => {
        if (!lessonForm.title || !lessonForm.video_url) {
            toast.error('Please fill in lesson details');
            return;
        }
        setFormData({
            ...formData,
            lessons: [...formData.lessons, { ...lessonForm, order: formData.lessons.length }],
        });
        setLessonForm({ title: '', description: '', video_url: '', duration: '', order: 0 });
    };

    const removeLesson = (index) => {
        setFormData({
            ...formData,
            lessons: formData.lessons.filter((_, i) => i !== index),
        });
    };

    return (
        <div className="p-8 max-w-5xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={() => navigate('/admin?tab=courses')}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>Back to Courses</span>
                </button>
                <h1 className="text-4xl font-extrabold text-slate-900">
                    {isEditing ? 'Edit Course' : 'Add New Course'}
                </h1>
                <p className="text-slate-600 mt-2">
                    {isEditing ? 'Update course information and content' : 'Create a new course for your platform'}
                </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Basic Information Card */}
                <div className="bg-white rounded-2xl border border-slate-100 p-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-6">Basic Information</h2>

                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Title <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="input-field"
                                required
                                placeholder="e.g., Complete Web Development Bootcamp"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Category <span className="text-red-600">*</span>
                            </label>
                            <select
                                value={formData.category_id}
                                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                                className="input-field"
                                required
                            >
                                <option value="">Select Category</option>
                                {MOCK_CATEGORIES.map((cat) => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mt-6">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Description <span className="text-red-600">*</span>
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="input-field min-h-32"
                            required
                            placeholder="Describe what students will learn in this course..."
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-6 mt-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Price ($) <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.price}
                                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                className="input-field"
                                required
                                placeholder="49.99"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Level</label>
                            <select
                                value={formData.level}
                                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                                className="input-field"
                            >
                                <option value="Beginner">Beginner</option>
                                <option value="Intermediate">Intermediate</option>
                                <option value="Advanced">Advanced</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6 mt-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Language</label>
                            <input
                                type="text"
                                value={formData.language}
                                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                                className="input-field"
                                placeholder="English"
                            />
                        </div>
                    </div>
                </div>

                {/* Media Card */}
                <div className="bg-white rounded-2xl border border-slate-100 p-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-6">Media</h2>

                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Thumbnail URL <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="url"
                                value={formData.thumbnail}
                                onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                                className="input-field"
                                required
                                placeholder="https://example.com/image.jpg"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Preview Video URL (optional)
                            </label>
                            <input
                                type="url"
                                value={formData.preview_video}
                                onChange={(e) => setFormData({ ...formData, preview_video: e.target.value })}
                                className="input-field"
                                placeholder="https://youtube.com/embed/..."
                            />
                        </div>
                    </div>
                </div>

                {/* Instructor Card */}
                <div className="bg-white rounded-2xl border border-slate-100 p-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-6">Instructor Information</h2>

                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Instructor Name <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.instructor}
                                onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                                className="input-field"
                                required
                                placeholder="John Doe"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Instructor Image URL <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="url"
                                value={formData.instructor_image}
                                onChange={(e) => setFormData({ ...formData, instructor_image: e.target.value })}
                                className="input-field"
                                required
                                placeholder="https://example.com/instructor.jpg"
                            />
                        </div>
                    </div>

                    <div className="mt-6">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Instructor Bio <span className="text-red-600">*</span>
                        </label>
                        <textarea
                            value={formData.instructor_bio}
                            onChange={(e) => setFormData({ ...formData, instructor_bio: e.target.value })}
                            className="input-field min-h-24"
                            required
                            placeholder="Brief bio about the instructor..."
                        />
                    </div>
                </div>

                {/* Lessons Card */}
                <div className="bg-white rounded-2xl border border-slate-100 p-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-6">Course Lessons</h2>

                    {/* Existing Lessons */}
                    <div className="space-y-4 mb-6">
                        {formData.lessons.map((lesson, index) => (
                            <div key={index} className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
                                <div>
                                    <div className="font-semibold text-slate-900">{lesson.title}</div>
                                    <div className="text-sm text-slate-600">{lesson.duration} min</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeLesson(index)}
                                    className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                        {formData.lessons.length === 0 && (
                            <div className="text-center py-8 text-slate-500">
                                No lessons added yet. Add your first lesson below.
                            </div>
                        )}
                    </div>

                    {/* Add New Lesson */}
                    <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                        <h3 className="text-lg font-bold text-slate-900 mb-4">Add New Lesson</h3>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    placeholder="Lesson title"
                                    value={lessonForm.title}
                                    onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                                    className="input-field"
                                />
                                <input
                                    type="number"
                                    placeholder="Duration (min)"
                                    value={lessonForm.duration}
                                    onChange={(e) => setLessonForm({ ...lessonForm, duration: e.target.value })}
                                    className="input-field"
                                />
                            </div>
                            <input
                                type="url"
                                placeholder="Video URL (YouTube embed)"
                                value={lessonForm.video_url}
                                onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })}
                                className="input-field"
                            />
                            <input
                                type="text"
                                placeholder="Lesson description"
                                value={lessonForm.description}
                                onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                                className="input-field"
                            />
                            <button
                                type="button"
                                onClick={addLesson}
                                className="btn-secondary w-full flex items-center justify-center gap-2"
                            >
                                <Plus className="w-5 h-5" />
                                Add Lesson
                            </button>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4 pt-4">
                    <button
                        type="button"
                        onClick={() => navigate('/admin?tab=courses')}
                        className="btn-secondary"
                    >
                        Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                        {isEditing ? 'Update Course' : 'Create Course'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default CourseFormPage;
